import { Link } from 'react-router-dom';
import {
  Clock, Facebook,
  Linkedin, Shield, Monitor, Cloud,
  Network, PenTool, Camera, Cable, Wrench, Phone, Upload, Award, Heart, FileCheck, ShieldCheck, Volume2,
  Lock, Wifi, Zap, BadgeCheck
} from 'lucide-react';
import { NAPDisplay } from '../seo/NAPConsistency';

// Version is hardcoded for now - can be dynamically set later
const version = "0.1.5";

export function Footer() {
  const services = [
    // Installation Services (70% of business - listed first)
    { name: 'Data Cabling', href: '/services/data-cabling', icon: Cable },
    { name: 'Security Cameras', href: '/services/security-cameras', icon: Camera },
    { name: 'Access Control', href: '/services/access-control', icon: Lock },
    { name: 'UniFi Installation', href: '/services/unifi-installation', icon: Wifi },
    { name: 'Low Voltage Cabling', href: '/services/low-voltage-cabling', icon: Zap },
    { name: 'Network Solutions', href: '/services/network', icon: Network },
    // IT Services (30% of business - listed after)
    { name: 'IT Support', href: '/services/it-support', icon: Monitor },
    { name: 'Cybersecurity', href: '/services/cybersecurity', icon: Shield },
    { name: 'Microsoft 365', href: '/services/microsoft-365', icon: Cloud },
    { name: 'VoIP Phone Systems', href: '/services/voip-phone-systems', icon: Phone },
    { name: 'Computer Repair', href: '/services/computer-repair', icon: Wrench }
  ];

  const serviceAreas = [
    'Meridian',
    'Boise',
    'Nampa',
    'Eagle',
    'Kuna',
    'Caldwell'
  ];

  return (
    <footer className="bg-gray-900 text-white pt-16 pb-8">
      <div className="container mx-auto px-4">
        {/* Trust Indicators Bar */}
        <div className="bg-gray-800/50 rounded-2xl p-6 mb-12 border border-gray-700">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-4">
            {/* 15-Minute Guarantee - NEW */}
            <div className="flex items-center space-x-2 col-span-2 md:col-span-1">
              <Clock className="w-8 h-8 text-primary-500" />
              <div>
                <p className="text-sm font-semibold text-primary-400">15-Min Guarantee</p>
                <p className="text-sm text-gray-300">24/7 Local IT</p>
              </div>
            </div>

            {/* BBB Accredited */}
            <div className="flex items-center space-x-2">
              <Shield className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm font-semibold">BBB Accredited</p>
                <p className="text-sm text-gray-300">A+ Rating</p>
              </div>
            </div>
            
            {/* Licensed Contractor */}
            <div className="flex items-center space-x-2">
              <FileCheck className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-sm font-semibold">Licensed</p>
                <p className="text-sm text-gray-300">#3171776</p>
              </div>
            </div>
            
            {/* Fully Insured */}
            <div className="flex items-center space-x-2">
              <ShieldCheck className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm font-semibold">Fully Insured</p>
                <p className="text-sm text-gray-300">Liability & Prof.</p>
              </div>
            </div>
            
            {/* Since 2005 */}
            <div className="flex items-center space-x-2">
              <Award className="w-8 h-8 text-primary-500" />
              <div>
                <p className="text-sm font-semibold">Since 2005</p>
                <p className="text-sm text-gray-300">20+ Years</p>
              </div>
            </div>
            
            {/* Chamber Member */}
            <div className="flex items-center space-x-2">
              <img src="/meridian-chamber-logo.svg" alt="Chamber" className="w-8 h-8 bg-white rounded p-1" />
              <div>
                <p className="text-sm font-semibold">Chamber Member</p>
                <p className="text-sm text-gray-300">Meridian & Boise</p>
              </div>
            </div>
            
            {/* Community Support */}
            <div className="flex items-center space-x-2">
              <Heart className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-sm font-semibold">Community</p>
                <p className="text-sm text-gray-300">Non-Profit Support</p>
              </div>
            </div>

            {/* BICSI Certified */}
            <div className="flex items-center space-x-2">
              <BadgeCheck className="w-8 h-8 text-emerald-500" />
              <div>
                <p className="text-sm font-semibold">BICSI Certified</p>
                <p className="text-sm text-gray-300">Prof. Installer</p>
              </div>
            </div>

            {/* Hanwha Vision STEP Partner */}
            <div className="flex items-center space-x-2">
              <Camera className="w-8 h-8 text-orange-500" />
              <div>
                <p className="text-sm font-semibold">Hanwha Vision</p>
                <p className="text-sm text-gray-300">STEP Partner</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Information */}
          <div>
            <NAPDisplay variant="footer" showEmail={true} />
            <div className="mt-6 space-y-3">
              <div className="flex items-start">
                <Clock className="w-5 h-5 mr-2 mt-1 flex-shrink-0 text-primary-400" />
                <div>
                  <p className="font-medium">Business Hours</p>
                  <p className="text-sm text-gray-300">Mon-Fri: 7:00 AM - 8:00 PM</p>
                  <p className="text-sm text-gray-300">Sat-Sun: 9:00 AM - 7:00 PM</p>
                  <p className="mt-2 text-primary-400 font-semibold">24/7 Emergency Support</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/services/data-cabling"
                  className="hover:text-primary-400 hover:underline transition-colors text-gray-200"
                >
                  Data Cabling
                </Link>
              </li>
              <li>
                <Link
                  to="/services/security-cameras"
                  className="hover:text-primary-400 hover:underline transition-colors text-gray-200"
                >
                  Security Cameras
                </Link>
              </li>
              <li>
                <Link
                  to="/services/access-control"
                  className="hover:text-primary-400 hover:underline transition-colors text-gray-200"
                >
                  Access Control
                </Link>
              </li>
              <li>
                <Link
                  to="/services/unifi-installation"
                  className="hover:text-primary-400 hover:underline transition-colors text-gray-200"
                >
                  UniFi Installation
                </Link>
              </li>
              <li>
                <Link
                  to="/services/it-support"
                  className="hover:text-primary-400 hover:underline transition-colors text-gray-200"
                >
                  IT Support
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="hover:text-primary-400 hover:underline transition-colors text-gray-200 font-medium"
                >
                  Technology Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="hover:text-primary-400 hover:underline transition-colors text-gray-200 font-medium"
                >
                  Get Quote
                </Link>
              </li>
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h3 className="text-xl font-bold mb-4">Service Areas</h3>
            <ul className="grid grid-cols-3 gap-2">
              {[
                { name: 'Boise', slug: 'boise' },
                { name: 'Meridian', slug: 'meridian' },
                { name: 'Nampa', slug: 'nampa' },
                { name: 'Eagle', slug: 'eagle' },
                { name: 'Caldwell', slug: 'caldwell' },
                { name: 'Kuna', slug: 'kuna' },
                { name: 'Star', slug: 'star' },
                { name: 'Garden City', slug: 'garden-city' },
                { name: 'Middleton', slug: 'middleton' },
              ].map((city) => (
                <li key={city.slug}>
                  <Link
                    to={`/location/${city.slug}`}
                    className="hover:text-primary-400 hover:underline transition-colors text-gray-200 text-sm"
                  >
                    {city.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-4 space-y-2">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Cabling by City</p>
              <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs">
                {[
                  { name: 'Boise', slug: 'boise' },
                  { name: 'Meridian', slug: 'meridian' },
                  { name: 'Nampa', slug: 'nampa' },
                  { name: 'Eagle', slug: 'eagle' },
                  { name: 'Caldwell', slug: 'caldwell' },
                  { name: 'Kuna', slug: 'kuna' },
                  { name: 'Star', slug: 'star' },
                  { name: 'Garden City', slug: 'garden-city' },
                  { name: 'Middleton', slug: 'middleton' },
                ].map((city, i, arr) => (
                  <span key={city.slug}>
                    <Link
                      to={`/location/${city.slug}/network-cabling`}
                      className="text-gray-300 hover:text-primary-400 hover:underline transition-colors"
                    >
                      {city.name}
                    </Link>
                    {i < arr.length - 1 && <span className="text-gray-600 ml-2">&middot;</span>}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mt-2">UniFi by City</p>
              <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs">
                {[
                  { name: 'Boise', slug: 'boise' },
                  { name: 'Meridian', slug: 'meridian' },
                  { name: 'Nampa', slug: 'nampa' },
                  { name: 'Eagle', slug: 'eagle' },
                  { name: 'Caldwell', slug: 'caldwell' },
                  { name: 'Kuna', slug: 'kuna' },
                  { name: 'Star', slug: 'star' },
                  { name: 'Garden City', slug: 'garden-city' },
                  { name: 'Middleton', slug: 'middleton' },
                ].map((city, i, arr) => (
                  <span key={city.slug}>
                    <Link
                      to={`/location/${city.slug}/unifi-installer`}
                      className="text-gray-300 hover:text-primary-400 hover:underline transition-colors"
                    >
                      {city.name}
                    </Link>
                    {i < arr.length - 1 && <span className="text-gray-600 ml-2">&middot;</span>}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Resources & Connect */}
          <div>
            <h3 className="text-xl font-bold mb-4">Resources</h3>
            <ul className="space-y-2 mb-6">
              <li>
                <Link to="/faq" className="hover:text-primary-400 hover:underline transition-colors text-gray-200">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/partners" className="hover:text-primary-400 hover:underline transition-colors text-gray-200">
                  Partners
                </Link>
              </li>
              <li>
                <Link to="/community" className="hover:text-primary-400 hover:underline transition-colors text-gray-200">
                  Community Support
                </Link>
              </li>
              <li>
                <Link to="/cybersecurity-checklist" className="hover:text-primary-400 hover:underline transition-colors text-gray-200">
                  Security Assessment
                </Link>
              </li>
              <li>
                <a
                  href="https://www.bbb.org/us/id/meridian/profile/computer-services/xxl-it-services-1296-1000013388"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary-400 hover:underline transition-colors text-gray-200"
                >
                  BBB Profile
                </a>
              </li>
            </ul>
            
            <h4 className="font-semibold mb-3">Follow Us</h4>
            <div className="flex space-x-4">
              <a 
                href="https://facebook.com/XXLIT" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-gray-800 p-2 rounded-lg hover:bg-gray-700 transition-colors"
                aria-label="Follow us on Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/95933739/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-gray-800 p-2 rounded-lg hover:bg-gray-700 transition-colors"
                aria-label="Follow us on LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-400">
              <p>© {new Date().getFullYear()} XXL IT Services. All rights reserved.</p>
              <p className="mt-1">Version {version}</p>
            </div>
            <div className="flex space-x-6 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors">
                Terms of Service
              </Link>
              {/* Static HTML page (public/sms-terms/index.html), not a React route */}
              <a href="/sms-terms" className="hover:text-white transition-colors">
                SMS Terms
              </a>
              <a
                href="/sitemap.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                Sitemap
              </a>
            </div>
          </div>

          {/* Powered By */}
          <div className="mt-6 pt-6 border-t border-gray-800/60 text-center">
            <p className="text-xs text-gray-500">
              © {new Date().getFullYear()} XXL IT SERVICES. Powered by{' '}
              <a
                href="https://mostailabs.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors"
              >
                Mostai Labs
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}